/**
 * Guest age brackets — one rule, read by the website, the city sites and the app.
 *
 * This lives in `shared` because the split is what caused the bug it replaces:
 * the app called an adult 12+ while the website called an adult 16+, so a family
 * with two 13-year-olds was quoted the adult price in one place and the child
 * price in the other, for the same booking. The bracket decides which price a
 * guest pays, so it cannot have two implementations.
 *
 * Returns translation keys and their params rather than finished strings: the
 * website nests its locale files and the app keeps them flat, and neither
 * should have to agree about that to agree about ages.
 */

/** Site-wide defaults. Owner decision, 2026-08-29: adults 16+, gap-free. */
export const DEFAULT_INFANT_MAX_AGE = 2;
export const DEFAULT_CHILDREN_MAX_AGE = 15;

export interface AgeBounds {
  /** Oldest age still counted as an infant. Null → site default. */
  infantMaxAge?: number | null;
  /** Oldest age still counted as a child. Null → site default. */
  childrenMaxAge?: number | null;
}

export interface AgeBracketLabel {
  key: string;
  params: Record<string, number>;
}

export interface AgeBracketLabels {
  adults: AgeBracketLabel;
  children: AgeBracketLabel;
  infants: AgeBracketLabel;
}

/**
 * Resolve the three brackets for one activity.
 *
 * Venues set their own bands: a pool that admits 13-year-olds at the adult rate
 * is a different business decision from a desert camp that does not, and
 * `activities.infant_max_age` / `children_max_age` exist so a venue can say so
 * without moving the site-wide default.
 *
 * Each bound falls back independently. The previous version required BOTH to be
 * set before it used either, which is why Alkamar Camp Pool — the one row in the
 * catalogue with a custom band (`children_max_age = 12`, `infant_max_age` null)
 * — silently rendered the site-wide labels instead of its own, on every surface.
 */
export function ageBracketLabels(bounds: AgeBounds = {}): AgeBracketLabels {
  const infantMax = normalize(bounds.infantMaxAge) ?? DEFAULT_INFANT_MAX_AGE;
  let childMax = normalize(bounds.childrenMaxAge) ?? DEFAULT_CHILDREN_MAX_AGE;

  // A child band that does not start after the infant band would print
  // nonsense ("3-2 years"). Treat the pair as unusable and give children at
  // least one year rather than rendering an empty range.
  if (childMax <= infantMax) childMax = infantMax + 1;

  return {
    infants: { key: "activity.infants_age_range", params: { max: infantMax } },
    children: {
      key: "activity.children_age_range",
      params: { min: infantMax + 1, max: childMax },
    },
    adults: { key: "activity.adults_age_from", params: { min: childMax + 1 } },
  };
}

/** English fallbacks, so a caller can render without a translator. */
export function ageBracketFallback(label: AgeBracketLabel): string {
  const { key, params } = label;
  if (key === "activity.infants_age_range") return `0-${params.max} years`;
  if (key === "activity.children_age_range") return `${params.min}-${params.max} years`;
  return `${params.min}+ years`;
}

function normalize(value: number | null | undefined): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  if (value < 0) return null;
  return Math.floor(value);
}
